// @ts-check

// @ts-ignore
import isString from "lodash/isString";
// @ts-ignore
import isArray from "lodash/isArray";
import arrayMerge from "../../../../../common/utils/arrayMerge";

/**
 * 由資料清單及 每頁比數, 計算總頁碼
 * @param data {*[]}
 * @param countPerPage {number}
 * @returns {number}
 */
export const getTotalPage = (data = [], countPerPage) => {
  const searchLen = Array.isArray(data) ? data.length : 0;
  const remainder = searchLen % countPerPage;
  const quotient = Math.floor(searchLen / countPerPage);
  let totalP = 1;
  if (remainder > 0) {
    totalP = quotient < 1 ? 1 : quotient + 1;
  } else {
    totalP = quotient < 1 ? 1 : quotient;
  }
  return totalP;
};

/**
 * 依據 page(目前頁碼)及 countPerPage(每頁數量)取得 limit, offset
 * @param page {number}
 * @param countPerPage {number}
 * @returns {{offset: number, limit: number}}
 */
export const getLimitOffset = (page, countPerPage) => ({
  limit: page * countPerPage,
  offset: (page - 1) * countPerPage,
});

/**
 * 依據 limit, offset 取得區間的 data
 * @param data {*[]}
 * @param limit {number}
 * @param offset {number}
 * @returns {*|*[]}
 */
export const getDataByLimitOffset = (data, limit, offset) =>
  (Array.isArray(data) &&
    data.filter((dt, idx) => idx >= offset && idx < limit)) ||
  [];

/**
 * 合併日誌事件(若某日的二個以上事件標記定位點相同時,則合併之)
 *
 * @param data {import('../../../SnaPage').ApiSnaDataList}
 * @returns {import('../../../SnaPage').ApiDiaryEventMergeList}
 */
export const mergeDiaryEvent = (data) => {
  if (!isArray(data)) return [];
  // merge data: 若事件 tag 的startPos及endPos相同時,則合併在一起
  // 其他資訊會放在 mergedList
  // 其他資訊: translator_id, translator, translator_lang
  // 		author_id, author, author_lang

  // date: "1929-01-01",
  //   evtStartPos: "162",
  //   evtEndPos: "168",
  //   perId: "PER2151",
  //   eventType: "EVTTYPE8",
  //   diary: "DIARY1929-01-01",
  //   eventTypeTagStartPos: "95",
  //   perLabel: "林猶龍",
  //   personTagEndPos: "164",
  //   eventTypeTag: "TAG_5729e77f-5f98-42a7-a051-0357c6e35006",
  //   personTagLabel: "猶龍",

  const mergedData = arrayMerge.timeline(
    data,
    ["graph", "diary", "date", "evtStartPos", "evtEndPos"],
    ["eventTypeTag", "eventType", "eventTypeName", "perId", "perLabel"],
    "eventTypeTags"
  );

  return mergedData.reduce(
    (
      /** @type {any[]} */ acc,
      /** @type {{
       * eventTypeTags: any[]; eventTypesName: any;
       * eventTypeTagsId: any; perIds: any;
       * perLabels:any;}} */ cur
    ) => {
      const tmpCur = { ...cur };
      if (isArray(cur.eventTypeTags)) {
        /**
         *
         * @param list {*[]}
         * @returns {*[]}
         */
        const uniqNames = (list) => Array.from(new Set(list));
        tmpCur.eventTypesName = uniqNames(
          cur.eventTypeTags.filter((o) => !!o).map((o) => o.eventTypeName)
        ).join(",");
        tmpCur.eventTypeTagsId = uniqNames(
          cur.eventTypeTags.filter((o) => !!o).map((o) => o.eventTypeTag)
        ).join(",");
        tmpCur.perIds = uniqNames(
          cur.eventTypeTags.filter((o) => !!o).map((o) => o.perId)
        ).join(",");
        tmpCur.perLabels = uniqNames(
          cur.eventTypeTags.filter((o) => !!o).map((o) => o.perLabel)
        ).join(",");
      }
      acc.push(tmpCur);
      return acc;
    },
    []
  );
};

/**
 * 依據 diary graph 取得 diary name
 * @param graph {string}
 * @param diaryList {import('../../../SnaPage').PublicDiaryList}
 * @returns {string|*|string}
 */
export const getDiaryName = (graph, diaryList) => {
  if (!Array.isArray(diaryList)) return "";
  const find = diaryList.find((o) => o.graph === graph);
  return find?.displayName || "";
};

/**
 *
 * @param eventList  {import('../../../SnaPage').ApiSnaDataList}
 * @param diaryList {import('../../../SnaPage').PublicDiaryList}
 * @returns {*}
 */
export const formatEventList = (eventList, diaryList) =>
  eventList.map((o) => ({
    ...o,
    id: o.eventTypeTag, // 建立 uniq id 以便 Table 偵測到該頁資料有變動時,可以通知 parent
    graphName: getDiaryName(o?.graph, diaryList),
  }));

/**
 * 從 val 中找出第一個符合的 separator
 *
 * @param val {string}
 * @param separator {[]|string}
 * @returns {null|string}
 */
const findSeparator = (val, separator = []) => {
  let findSep = null;
  if (!(isString(val) && val.length > 0)) return findSep;
  let stop = false;
  const separators = Array.isArray(separator) ? separator : [separator];
  separators.forEach((o) => {
    if (!stop && val.indexOf(o) >= 0) {
      findSep = o;
      stop = true;
    }
  });
  return findSep;
};

/**
 * 將文字依據 separator 拆開成 list
 * @param val {string|any}
 * @param separator {string|[]}  e.g. "\n"  or ['、', ',', '，']
 * @returns {*|*[]}
 * * 範例:
 *  * authors: Pai Chiu__PER2733__en\nCheng Chou-yu__PER2733__en\nLo Fu__PER2733__en
 *  * => ["Pai Chiu__PER2733__en","Cheng Chou-yu__PER2733__en","Lo Fu__PER2733__en"],
 *  * */
export const splitVal = (val, separator = "\n") => {
  if (!(isString(val) && val.length > 0)) return [];
  const findSep = findSeparator(val, separator);
  return findSep ? val.split(findSep) : [val];
};
